import { v4 as uuidv4 } from 'uuid';
import {
  useState, useEffect, useMemo, useRef, Dispatch, SetStateAction,
} from 'react';
import { useSelector } from 'react-redux';
import {
  Page,
  Column,
  Row,
  Spacer,
  Flex,
  Empty,
} from '@oliasoft-open-source/react-ui-library';
import { DataDrawer } from 'client/components/data-viewer/data-drawer';
import { ConnectedProps, connect, useDispatch } from 'react-redux';
import {
  getNodes, createNode, deleteNode, deleteDesignNode, addDesignNode,
} from 'client/store/entities/hierarchy/node/node';
import { getArchives } from 'client/store/entities/hierarchy/archive/archive';
import { RootState } from 'client/store/configureStore';
import { hierarchyReset } from 'client/store/entities/hierarchy-state-slice-factory';
import { HierarchyLevelType, IObject } from 'common/interfaces/hierarchy-entities';
import {
  setSelectedArchiveId, setActiveItem, addToOpenedItems, setAfterUnpackedActiveArchiveState,
} from 'client/store/ui/hierarchy-tree/hierarchy-tree';
import { useParams, useSearchParams } from 'react-router-dom';
import { getHierarchyEntityChainAPICallAction } from 'client/store/entities/hierarchy/helpers/hieararchy-api-actions-types';
import { getEntityChildren } from 'client/api-utils';
import {
  HierarchyModals, IHierarchyModalsRef, IUnitTemplate, TTranslations,
} from '@oliasoft/hierarchy-gui';
import { DeleteModal } from 'client/components/delete.modal';
import { PossibleEDMDTables, RecognisedDataCatalogues } from 'common/enums/known-data-enums';
import { isMultipleTableType } from 'common/utils/is-multiple-tables';
import { clearData } from 'client/store/entities/object/object';
import { EntityType } from 'common/enums/entity-enums';
import { HierarchyDrawer } from './hierarchy/hierarchy-drawer';
import { HierarchyBreadcrumb } from './hierarchy/hierarchy-breadcrumb';
import {
  combineHierarchy,
  generateDesignHierarchyData,
  generateHierarchyTree,
  getChildHierarchyEntityType,
  getParentHierarchyEntityType,
} from './hierarchy/hierarchy.utils';
import { IHierarchyTreeItem } from './hierarchy/hierarchy.interfaces';
import { HierarchyItemStates } from './hierarchy/hierarchy-enums';
import {
  getCreateEntityAction, getDefaultNewEntity, getManyEntitiesAction, getOneEntityAction, getUpdateEntityAction, mapTreeItemToEntity, deleteEntityAction,
} from './hierarchy/hierarchy-entities-factory';
import { EntityFormModal } from './hierarchy-forms/entity-form';
import { DataExplorer } from './data-explorer/data-explorer';
import { HierchyAfterUnpackedActiveModal } from './hierarchy-forms/hierarchy-after-unpacked-active.modal';
import { getObjectsByTypeAndParent } from 'client/store/entities/object/selectors';
import { DesignSyncControls } from 'client/components/designs/design-sync-controls';
import { MultipleLinkedObjectModal } from 'client/components/object/multiple-linked-object-modal';
import { AssociatedDataModal } from 'client/components/data-viewer/associated-data-viewer/associated-data-modal';
import { ModalType } from 'common/enums/information-enums';

const selectedEntityTypes = {
  [HierarchyLevelType.Archive]: 'selectedArchive',
  [HierarchyLevelType.Country]: 'selectedCountry',
  [HierarchyLevelType.Field]: 'selectedField',
  [HierarchyLevelType.Site]: 'selectedSite',
  [HierarchyLevelType.Well]: 'selectedWell',
  [HierarchyLevelType.Wellbore]: 'selectedWellbore',
  [HierarchyLevelType.Design]: 'selectedDesign',
};

type SelectedEntityType = keyof typeof selectedEntityTypes;

const Explore = ({
  hierarchyReset,
  archives,
  getArchives,
  archivesLoading,
  countries,
  countriesLoading,
  countriesPages,
  fields,
  fieldsLoading,
  fieldsPages,
  sites,
  sitesLoading,
  sitesPages,
  wells,
  wellsLoading,
  wellsPages,
  wellbores,
  wellboresLoading,
  wellboresPages,
  designs,
  designsLoading,
  designsPages,
  nodesLoading,
  nodesPages,
  nodes,
  getNodes,
  createNode,
  deleteNode,
  deleteDesignNode,
  addDesignNode,
  deleteEntityAction,
  countryLengths,
  fieldLengths,
  siteLengths,
  wellLengths,
  wellboreLengths,
  designLengths,
  selectedArchiveId,
  setSelectedArchiveId,
  activeItem,
  setActiveItem,
  getHierarchyEntityChainAPICallAction,
  addToOpenedItems,
  isAfterUnpackedActiveArchive,
  setAfterUnpackedActiveArchiveState,
  openedItems,
  selectedCustomer,
  clearData,
  objects,
}: PropsFromRedux) => {
  const childRef = useRef<IHierarchyModalsRef>(null);
  const { companyId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedKey, setSelectedKey] = useState<{ objectid: string; nodeid: string } | null>(null);
  const [selectedMultipleLinkedItems, setSelectedMultipleLinkedItems] = useState<IObject[]>([]);
  const [multipleLinkedObjectsModalState, setMultipleLinkedObjectsModalState] = useState<boolean>(false);
  const [associatedDataModalState, setAssociatedDataModalState] = useState<boolean>(false);

  const isDesignLevel = activeItem?.entityType === HierarchyLevelType.Design;

  const handleOpenTree = (obj: IObject) => {
    if (obj.type === EntityType.CovarianceMatrix) {
      const covarianceMatrixNode = nodes.find(n => n.name === RecognisedDataCatalogues.CovarianceMatrixTotal);
      setOpenTreeNodes([obj.nodeid, covarianceMatrixNode?.nodeid!]);
    } else {
      setOpenTreeNodes([obj.nodeid])
    }
  }

  const selectedData = selectedKey
    ? (objects?.[selectedKey.nodeid]?.find((o) => o.objectid === selectedKey.objectid) ?? null)
    : null;
  const setSelectedData: Dispatch<SetStateAction<IObject | null>> = (action) => {
    const obj = typeof action === 'function' ? action(selectedData) : action;
    setSelectedKey(obj ? { objectid: obj.objectid, nodeid: obj.nodeid } : null);
    if (obj) {
      handleOpenTree(obj)
    }
  };

  const [openTreeNodes, setOpenTreeNodes] = useState<string[]>([]);
  const [dataViewerVisible, setDataViewerVisible] = useState(false);
  const [groupByLevel, setGroupByLevel] = useState(HierarchyLevelType.Country);

  const getTotalChildCount = (loadType: HierarchyLevelType, parentId: string) => {
    switch (loadType) {
      case HierarchyLevelType.Country: {
        const count = countryLengths[parentId];
        return count;
      }
      case HierarchyLevelType.Field: {
        const count = fieldLengths[parentId];
        return count;
      }
      case HierarchyLevelType.Site: {
        const count = siteLengths[parentId];
        return count;
      }
      case HierarchyLevelType.Well: {
        const count = wellLengths[parentId];
        return count;
      }
      case HierarchyLevelType.Wellbore: {
        const count = wellboreLengths[parentId];
        return count;
      }
      case HierarchyLevelType.Design: {
        const count = designLengths[parentId];
        return count;
      }
    }
  };

  const getLoadManyParams = (loadType: HierarchyLevelType, parentId: string) => {
    const result = {
      parentId,
      parenttype: groupByLevel === loadType ? HierarchyLevelType.Archive : undefined,
      page: 1,
    };
    switch (loadType) {
      case HierarchyLevelType.Country: {
        result.page = countriesPages[parentId] || 1;
        break;
      }
      case HierarchyLevelType.Field: {
        result.page = fieldsPages[parentId] || 1;
        break;
      }
      case HierarchyLevelType.Site: {
        result.page = sitesPages[parentId] || 1;
        break;
      }
      case HierarchyLevelType.Well: {
        result.page = wellsPages[parentId] || 1;
        break;
      }
      case HierarchyLevelType.Wellbore: {
        result.page = wellboresPages[parentId] || 1;
        break;
      }
      case HierarchyLevelType.Design: {
        result.page = designsPages[parentId] || 1;
        break;
      }
      case HierarchyLevelType.Node: {
        result.page = nodesPages[parentId] || 1;
        break;
      }
      default: {
        throw new Error('Not implemented');
      }
    }
    return result;
  };

  const dispatch = useDispatch();

  const loadMoreChildren = (parentItem: Pick<IHierarchyTreeItem, 'id' | 'entityType' | 'nodeId'>) => {
    if (parentItem.entityType !== HierarchyLevelType.Node) {
      const loadType = getChildHierarchyEntityType(parentItem.entityType);
      if (loadType === HierarchyLevelType.Node) return;
      const parentId = parentItem.id || '';
      const params = getLoadManyParams(loadType!, parentId);
      dispatch(getManyEntitiesAction(loadType!, params));
    }
  };

  const loadActiveItemNodes = (item: Pick<IHierarchyTreeItem, 'id' | 'entityType'>) => {
    if (item.entityType === HierarchyLevelType.Archive || item.entityType === HierarchyLevelType.Design) return;
    const nodesParams = getLoadManyParams(HierarchyLevelType.Node, item.id);
    getNodes({ ...nodesParams, parenttype: item.entityType });
  };
  const setHierarchyItem = (item: IHierarchyTreeItem) => {
    const { onClick, ...itemCLean } = item;
    setActiveItem(itemCLean);
    setSelectedData(null);
    if (groupByLevel !== HierarchyLevelType.Country) {
      getHierarchyEntityChainAPICallAction(item.id, item.entityType as HierarchyLevelType, (loadedEntityId: string, type: HierarchyLevelType) => {
        addToOpenedItems(loadedEntityId);
        loadMoreChildren({ id: loadedEntityId, entityType: type });
        loadMoreChildren({ id: selectedArchiveId!, entityType: HierarchyLevelType.Archive });
      });
    }
  };

  const hierarchyTree = useMemo(() => {
    if (!archives) return;
    const combinedHierarchy = combineHierarchy(archives, countries, fields, sites, wells, wellbores, designs);
    const tree = generateHierarchyTree(combinedHierarchy, setHierarchyItem);
    return tree;
  }, [archives, countries, fields, sites, wells, wellbores, designs]);

  const hierarchy = useMemo(() => ({
    countries, fields, sites, wells, wellbores,
  }), [countries, fields, sites, wells, wellbores]);

  useEffect(() => {
    if (selectedCustomer && selectedCustomer.companyId === companyId) {
      setSelectedArchiveId('');
      getArchives();
      const activeArhiveId = searchParams.get('activeArhiveId');
      const activeEntityType = searchParams.get('activeEntityType');
      const activeEntityId = searchParams.get('activeEntityId');
      if (!activeArhiveId || !activeEntityId || !activeEntityType) return;
      setSelectedArchiveId(activeArhiveId);
      setActiveItem({ id: activeEntityId, entityType: activeEntityType as HierarchyLevelType });
      addToOpenedItems(activeEntityId);
      if (activeEntityType !== HierarchyLevelType.Country) {
        getHierarchyEntityChainAPICallAction(activeEntityId, activeEntityType as HierarchyLevelType, (loadedEntityId: string, type: HierarchyLevelType) => {
          addToOpenedItems(loadedEntityId);
          loadMoreChildren({ id: loadedEntityId, entityType: type });
        });
      }
    }
  }, [selectedCustomer, companyId]);

  useEffect(() => {
    if (!archives) return;
    if (selectedArchiveId) {
      setSearchParams({
        activeArhiveId: selectedArchiveId,
        activeEntityId: selectedArchiveId,
        activeEntityType: HierarchyLevelType.Archive,
      });
      return;
    }

    setSelectedArchiveId(archives[0]?.archiveid);
  }, [archives, selectedArchiveId]);

  useEffect(() => {
    if (!hierarchyTree || !activeItem) return;
    setSearchParams({
      activeArhiveId: selectedArchiveId!,
      activeEntityId: activeItem.id,
      activeEntityType: activeItem.entityType,
    });
  }, [activeItem, hierarchyTree]);

  useEffect(() => {
    if (!selectedArchiveId || !selectedCustomer) return;
    hierarchyReset();
    loadMoreChildren({ id: selectedArchiveId, entityType: getParentHierarchyEntityType(groupByLevel)! });
  }, [selectedArchiveId, groupByLevel, selectedCustomer]);

  const [prevActiveId, setPrevActiveId] = useState<string | null>(null);
  useEffect(() => {
    if (!activeItem || !hierarchyTree || activeItem.id === prevActiveId || activeItem.entityType === HierarchyLevelType.Archive || !hierarchyTree[activeItem.id]) return;

    setPrevActiveId(activeItem.id);
    loadMoreChildren(activeItem);
    loadActiveItemNodes(activeItem);
  }, [activeItem, hierarchyTree]);

  const [selectedItem, setSelectedItem] = useState<Partial<IHierarchyTreeItem> | null>(null);
  // projectData of the design being edited, which is not necessarily the active design
  const editedDesignId = selectedItem?.entityType === HierarchyLevelType.Design ? selectedItem.id : null;
  const [projectData] = useSelector(state => getObjectsByTypeAndParent(state, EntityType.ProjectData, HierarchyLevelType.Design, editedDesignId ?? ''));
  const [editingItemParent, setEditingItemParent] = useState<IHierarchyTreeItem | null>(null);
  const [itemState, setItemState] = useState<HierarchyItemStates | null>(null);

  const getSelection = (selectedItem: Partial<IHierarchyTreeItem>) => {
    if (!selectedItem?.entityType || !hierarchyTree) return {};
    const selection = {} as typeof selectedEntityTypes;

    while (selectedItem) {
      const item = hierarchyTree[selectedItem.parentId as string];
      if (item) {
        const entityType = selectedEntityTypes[item.entityType as SelectedEntityType];
        selection[entityType as SelectedEntityType] = item.id;
      }
      selectedItem = item;
    }

    return selection;
  };

  const useHierarchyModal = [HierarchyLevelType.Country, HierarchyLevelType.Field, HierarchyLevelType.Site, HierarchyLevelType.Well, HierarchyLevelType.Wellbore].includes(selectedItem?.entityType as HierarchyLevelType);

  const isEditItem = itemState === HierarchyItemStates.Edit;
  const isCreateItem = itemState === HierarchyItemStates.Create;
  const isDeleteItem = itemState === HierarchyItemStates.Delete;

  const saveEntity = async (entity: { type: string, payload: Partial<IHierarchyTreeItem> }) => {
    if (!hierarchyTree) return;

    if (selectedItem?.entityType === HierarchyLevelType.Design) {
      const hierarchyData = generateDesignHierarchyData(entity.payload?.parentId, Object.values(hierarchyTree));
      const selectedArchive = archives?.find(({ archiveid }: { archiveid: string }) => archiveid === selectedArchiveId);
      entity.payload.metadata = { hierarchyData: { ...hierarchyData, [HierarchyLevelType.Archive]: { id: selectedArchiveId!, name: selectedArchive?.name || '' } } };

      if (isEditItem) {
        entity.payload.nodeId = projectData?.nodeid;
      }
    }

    if (selectedItem?.entityType === HierarchyLevelType.Country) {
      entity.payload.parentId = selectedArchiveId;
    }

    if (isEditItem) {
      dispatch(getUpdateEntityAction({ ...entity.payload, entityType: entity.type as HierarchyLevelType })!);
    } else if (entity.type === HierarchyLevelType.Node) {
      const isParentNode = editingItemParent!.entityType === HierarchyLevelType.Node;
      // @ts-ignore
      const newNode = mapTreeItemToEntity({
        ...entity.payload,
        ...(isParentNode && { parentid: editingItemParent!.id }),
        ...(!isParentNode && { parent: { type: editingItemParent!.entityType, id: editingItemParent!.id } }),
      });
      createNode(newNode, (nodeid: string) => {
        if (!isParentNode) {
          dispatch(getOneEntityAction(editingItemParent!));
        }
        if (isDesignLevel) {
          const data = { designid: activeItem.id, nodeid };
          addDesignNode(data);
        }
      });
    } else {
      dispatch(getCreateEntityAction({ ...entity.payload, entityType: entity.type as HierarchyLevelType }));
    }
    setSelectedItem(null);
  };

  const closeModal = () => {
    setItemState(null);
    setSelectedItem(null);
  };

  const onDelete = () => {
    if (!hierarchyTree) return;
    const { entityType, type } = selectedItem!;
    const isStructuredObject = type ? type in PossibleEDMDTables : false;

    if (entityType === HierarchyLevelType.Node) {
      if (!isStructuredObject) {
        deleteNode(selectedItem!);
      }
      if (isDesignLevel && selectedItem!.parent === 0) {
        const data = { nodeid: selectedItem!.id!, designid: activeItem.id };
        deleteDesignNode(data);
      }
    } else if (isStructuredObject && !isMultipleTableType(type as EntityType)) {
      // @ts-ignore
      clearData(mapTreeItemToEntity(selectedItem!));
      setSelectedItem(null);
    } else {
      deleteEntityAction(selectedItem!);
    }
    if (entityType === HierarchyLevelType.Archive) {
      setSelectedArchiveId(null);
    }

    closeModal();
  };

  const createItem = (entityType: HierarchyLevelType, parentItem: IHierarchyTreeItem | null) => {
    const newDefaultItem = getDefaultNewEntity(entityType, parentItem?.id);
    setEditingItemParent(parentItem);
    setSelectedItem(newDefaultItem);
    setItemState(HierarchyItemStates.Create);

    switch (newDefaultItem.entityType) {
      case HierarchyLevelType.Country: {
        childRef.current?.countryAdditionStarted({ countryid: uuidv4() });
        break;
      }
      case HierarchyLevelType.Field: {
        const country = hierarchy?.countries?.find(({ countryid }: { countryid: string }) => countryid === parentItem?.id);
        if (country) {
          childRef.current?.fieldAdditionStarted({ country, fieldid: uuidv4() });
        }
        break;
      }
      case HierarchyLevelType.Site: {
        const field = hierarchy?.fields?.find(({ fieldid }: { fieldid: string }) => fieldid === parentItem?.id);
        if (field) {
          childRef.current?.siteAdditionStarted({ field, siteid: uuidv4() });
        }
        break;
      }
      case HierarchyLevelType.Well: {
        const site = hierarchy?.sites?.find(({ siteid }: { siteid: string }) => siteid === parentItem?.id);
        if (site) {
          childRef.current?.wellAdditionStarted({ site, wellid: uuidv4() });
        }
        break;
      }
      case HierarchyLevelType.Wellbore: {
        const wellid = parentItem?.id;
        if (wellid) {
          childRef.current?.wellBoreAdditionStarted({ wellid, wellboreid: uuidv4() });
        }
        break;
      }
    }
  };

  const editItem = (item: IHierarchyTreeItem, type: HierarchyItemStates) => {
    setSelectedItem(item);
    setItemState(type);

    switch (item.entityType) {
      case HierarchyLevelType.Country: {
        const country = hierarchy?.countries?.find(({ countryid }: { countryid: string }) => countryid === item?.id);
        if (country) {
          childRef.current?.countryConfigurationStarted(country);
        }
        break;
      }
      case HierarchyLevelType.Field: {
        const field = hierarchy?.fields?.find(({ fieldid }: { fieldid: string }) => fieldid === item?.id);
        if (field) {
          childRef.current?.fieldConfigurationStarted(field);
        }
        break;
      }
      case HierarchyLevelType.Site: {
        const site = hierarchy?.sites?.find(({ siteid }: { siteid: string }) => siteid === item?.id);
        if (site) {
          childRef.current?.siteConfigurationStarted(site);
        }
        break;
      }
      case HierarchyLevelType.Well: {
        const well = hierarchy?.wells?.find(({ wellid }: { wellid: string }) => wellid === item?.id);
        if (well) {
          childRef.current?.wellConfigurationStarted(well);
        }
        break;
      }
      case HierarchyLevelType.Wellbore: {
        const wellbore = hierarchy?.wellbores?.find(({ wellboreid }: { wellboreid: string }) => wellboreid === item?.id);
        if (wellbore) {
          childRef.current?.wellBoreConfigurationStarted(wellbore);
        }
        break;
      }
    }
  };

  const deleteItem = (item: IHierarchyTreeItem, type: HierarchyItemStates) => {
    setSelectedItem(item);
    setItemState(type);
  };

  const setHierarchyGroupping = (groupBy: HierarchyLevelType) => {
    setGroupByLevel(groupBy);
  };

  const getIsLoading = (item: IHierarchyTreeItem | null) => {
    if (!item) return archivesLoading;
    switch (item.entityType) {
      case HierarchyLevelType.Archive:
        return countriesLoading[item.id];
      case HierarchyLevelType.Country:
        return fieldsLoading[item.id];
      case HierarchyLevelType.Field:
        return sitesLoading[item.id];
      case HierarchyLevelType.Site:
        return wellsLoading[item.id];
      case HierarchyLevelType.Well:
        return wellboresLoading[item.id];
      case HierarchyLevelType.Wellbore:
        return designsLoading[item.id];
      case HierarchyLevelType.Node:
        return nodesLoading[item.id];
    }
    return false;
  };

  const empty = <Flex alignItems="center" justifyContent="center"><Empty text="Select an item on the left to view files" /></Flex>;
  const selection = getSelection(selectedItem!);

  const onAfterUnpackedActiveArchive = (needRefresh: boolean) => {
    setAfterUnpackedActiveArchiveState(false);
    if (!needRefresh) return;
    if (!hierarchyTree) return;
    const itemsToReload = Object.keys(openedItems).map(itemId => hierarchyTree[itemId]);
    hierarchyReset(); // reset loaded items
    if (groupByLevel === HierarchyLevelType.Country) { // reload countries if need
      loadMoreChildren({ id: selectedArchiveId!, entityType: HierarchyLevelType.Archive });
    }
    itemsToReload.forEach(item => loadMoreChildren(item)); // reload items that was opened before
    if (activeItem) {   // reload selected item's children and catalogues

      loadMoreChildren(activeItem);
      loadActiveItemNodes(activeItem);
    }
  };

  // TODO get settings from configuration ms
  const settings = {
    configuration: {
      well_configuration: '',
    },
    labelsSettings: {
      type: '',
    },
    countrySettings: {
      location: {
        isLocked: false,
      },
    },
    fieldSettings: {
      location: {
        isLocked: false,
      },
      lockName: false,
    },
    wellSettings: {
      lockName: false,
      location: {
        isLocked: false,
        lockType: false,
        lockCoordinates: false,
        lockGridScale: false,
        lockNorthReference: false,
      },
      identifierInputs: {
        isLocked: false,
      },
      uncertainty: {
        isLocked: false,
      },
      template: {
        isLocked: false,
      },
    },
    siteSettings: {
      lockName: false,
      location: {
        lockCoordinates: false,
        lockGridScale: false,
        lockNorthReference: false,
      },
      uncertainty: {
        isLocked: false,
      },
      template: {
        isLocked: false,
      },
    },
    wellboreSettings: {
      lockName: false,
      identifierInputs: {
        isLocked: false,
      },
    },
    location: {
      isLocked: false,
    },
    hierarchyContentSettings: {
      wellContentSettings: {
        showLegalName: true,
        showUwi: true,
        showWellboreNo: true,
        showApiN: true,
        showApiSuffixNo: true,
        showApiNo: true,
      },
      wellboreContentSettings: {
        showLegalName: true,
        showUwi: true,
        showWellboreNo: true,
        showApiN: true,
        showApiSuffixNo: true,
        showApiNo: true,
      },
    },
  };

  const getAffectedSitesAndWells = async (fieldid: string) => {
    if (!fieldid) {
      return {
        wells: [],
        sites: [],
      };
    }

    const affectedItems = await getEntityChildren(HierarchyLevelType.Field, fieldid);
    return {
      sites: affectedItems?.sites || [],
      wells: affectedItems?.wells || [],
    };
  };

  return (
    <Page padding={false} scroll={false}>
      <HierarchyModals
        ref={childRef}
        // setting
        settings={settings} // settings microservice data
        hierarchy={hierarchy}
        // currently active items for project specific
        selection={selection as any}
        // loaders from your local state redux/whatever
        isCountryLoading={false}
        isFieldLoading={false}
        isSiteLoading={false}
        isWellLoading={false}
        isWellBoreLoading={false}
        translations={{} as TTranslations}
        // common
        handleHierarchyItemAdded={saveEntity} // function that wil return object to create
        handleHierarchyItemEdited={saveEntity} // function that wil return object to update
        //
        reservoirFeatureFlagEnabled // should not be here but added just not to brake existing hidden functionality in welldesign
        getAffectedSitesAndWells={getAffectedSitesAndWells} // function that will return children of specific field
        ReservoirModelsComponent={Spacer} // TODO replace with proper implementation
        DepthDiagramRender={Spacer} // TODO replace with proper implementation
        unitTemplate={{} as IUnitTemplate} // TODO replace with proper implementation
        SideTrackSection={() => null}
        lockers={
          {
            isCountryLocationLocked: false,
            isFieldNameLocked: false,
            isFieldLocationLocked: false,
            isSlotLocked: false,
            isSiteNameLocked: false,
            isTemplateLocked: false,
            isSiteUncertaintyLocked: false,
            isCoordinateLocked: false,
            isApplyScaleLocked: false,
            isNorthReferenceLocked: false,
            isWellNameLocked: false,
            isWellCoordinateLocked: false,
            isWellTypeLocked: false,
            isWellUncertaintyLocked: false,
            isUwiInputDisabled: false,
            isWellBoreNameLocked: false,
            isUwiWellboreInputDisabled: false,
          }
        }
      />

      { !useHierarchyModal && (isCreateItem || isEditItem) && selectedItem && <EntityFormModal entity={selectedItem} isEdit={isEditItem} saveEntity={saveEntity} close={closeModal} />}
      { isDeleteItem && selectedItem && <DeleteModal entity={selectedItem} onSubmit={onDelete} onClose={closeModal} />}
      { isAfterUnpackedActiveArchive && <HierchyAfterUnpackedActiveModal onSubmit={() => onAfterUnpackedActiveArchive(true)} onClose={() => onAfterUnpackedActiveArchive(false)} />}
      {multipleLinkedObjectsModalState && <MultipleLinkedObjectModal
        visible={multipleLinkedObjectsModalState}
        linkedItems={selectedMultipleLinkedItems}
        linkedObject={selectedData!}
        onNavigate={(item) => {
          setSelectedData(item);
          setMultipleLinkedObjectsModalState(false)
        }}
        onClose={() => setMultipleLinkedObjectsModalState(false)} />
      }
      {associatedDataModalState && <AssociatedDataModal
        linkedItems={selectedMultipleLinkedItems}
        dataObject={selectedData}
        onClose={() => setAssociatedDataModalState(false)}
        setSelectedData={setSelectedData}
      />
      }
      <Row spacing={0} height="100%">
        <HierarchyDrawer
          hierarchy={hierarchyTree || {}}
          topLevel={groupByLevel}
          setTopLevel={setHierarchyGroupping}
          getIsLoading={getIsLoading}
          loadMore={loadMoreChildren}
          createItem={createItem}
          editItem={editItem}
          onItemDelete={deleteItem}
          archives={archives || []}
          getTotalChildCount={getTotalChildCount}
        />
        <Column padding="var(--padding)" scroll>
          <Row>
            <div style={{ width: '100%', padding: '20px' }}>
              <Flex justifyContent="space-between">
                <HierarchyBreadcrumb
                  hierarchy={hierarchyTree || {}}
                  selectedItemId={activeItem?.id || null}
                  topLevel={HierarchyLevelType.Country}
                  onClick={setHierarchyItem}
                />
                <DesignSyncControls activeItem={activeItem} />
              </Flex>
            </div>
          </Row>
          <Spacer />
          {activeItem ? (
            <DataExplorer
              selectedData={selectedData}
              setSelectedData={setSelectedData}
              hierarchy={hierarchyTree || {}}
              createItem={createItem}
              editItem={editItem}
              deleteItem={deleteItem}
              setDataViewerVisible={setDataViewerVisible}
              loadMore={loadMoreChildren}
              getIsLoading={getIsLoading}
              getTotalChildCount={getTotalChildCount}
              openTreeNodes={openTreeNodes}
            />
          ) : empty}
        </Column>
        <DataDrawer
          dataObject={selectedData}
          visible={dataViewerVisible}
          setVisible={setDataViewerVisible}
          setSelectedData={setSelectedData}
          setSelectedMultipleLinkedItems={(objects, modalType) => {
            const setModalState = {
              [ModalType.AssociatedData]: setAssociatedDataModalState,
              [ModalType.MultipleLinkedItems]: setMultipleLinkedObjectsModalState,
            };

            setSelectedMultipleLinkedItems(objects);
            setModalState[modalType](true);
          }}
          archiveid={selectedArchiveId!}
        />
      </Row>
    </Page>
  );
};

const mapStateToProps = ({ entities, ui }: RootState) => ({
  archives: entities?.archive.archives,
  archivesLoading: entities?.archive.isLoading,
  countries: entities?.country.items,
  countriesLoading: entities?.country.isLoading,
  countriesPages: entities?.country.pages,
  fields: entities?.field.items,
  fieldsLoading: entities?.field.isLoading,
  fieldsPages: entities?.field.pages,
  sites: entities?.site.items,
  sitesLoading: entities?.site.isLoading,
  sitesPages: entities?.site.pages,
  wells: entities.well.items,
  wellsLoading: entities?.well.isLoading,
  wellsPages: entities.well.pages,
  wellbores: entities?.wellbore.items,
  wellboresLoading: entities?.wellbore.isLoading,
  wellboresPages: entities?.wellbore.pages,
  designs: entities?.design.items,
  designsLoading: entities?.design.isLoading,
  designsPages: entities?.design.pages,
  selectedCustomer: entities?.customer.selectedCustomer,
  objects: entities?.object.objects,
  nodesLoading: entities?.nodeTree.isLoading,
  nodesPages: entities?.nodeTree.pages,
  nodes: entities?.nodeTree.items,
  countryLengths: entities?.country.lengths,
  fieldLengths: entities?.field.lengths,
  siteLengths: entities?.site.lengths,
  wellLengths: entities?.well.lengths,
  wellboreLengths: entities?.wellbore.lengths,
  designLengths: entities?.design.lengths,
  selectedArchiveId: ui.hierarchyTree.selectedArchiveId,
  activeItem: ui.hierarchyTree.activeItem,
  isAfterUnpackedActiveArchive: ui.hierarchyTree.isAfterUnpackedActiveArchive,
  openedItems: ui.hierarchyTree.openedItems,
});

const mapDispatchToProps = {
  hierarchyReset,
  getNodes,
  createNode,
  deleteNode,
  deleteEntityAction,
  deleteDesignNode,
  addDesignNode,
  setSelectedArchiveId,
  getArchives,
  setActiveItem,
  getHierarchyEntityChainAPICallAction,
  addToOpenedItems,
  setAfterUnpackedActiveArchiveState,
  clearData,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Container = connector(Explore);

export { Container as Explore };
